import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatApiError } from "../lib/api";
import { Plus, Pencil, Archive, RotateCcw, X } from "lucide-react";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const WEEKDAY_LABELS = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
  fri: "Friday", sat: "Saturday", sun: "Sunday",
};
const WEEKDAY_SHORT = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const blank = {
  display_name: "",
  bio: "",
  photo_url: "",
  working_days: ["mon", "tue", "wed", "thu", "fri"],
  service_ids: [],
  active: true,
};

export default function Practitioners() {
  const [practitioners, setPractitioners] = useState([]);
  const [dayRules, setDayRules] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | <id>
  const [draft, setDraft] = useState(blank);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/practitioners").then(({ data }) => setPractitioners(data));
    api.get("/day-rules").then(({ data }) => setDayRules(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setRule = async (weekday, practitioner_id) => {
    if (!practitioner_id) {
      // "— Closed —" was selected → delete any existing rule for this weekday
      const existing = dayRules.find((r) => r.weekday === weekday);
      if (existing) {
        await api.delete(`/day-rules/${existing.id}`);
        load();
      }
      return;
    }
    await api.post("/day-rules", { weekday, practitioner_id });
    load();
  };
  const findRule = (weekday) => dayRules.find((r) => r.weekday === weekday)?.practitioner_id || "";

  const startNew = () => { setError(""); setEditing("new"); setDraft({ ...blank }); };
  const startEdit = (p) => {
    setError("");
    setEditing(p.id);
    setDraft({
      display_name: p.display_name || "",
      bio: p.bio || "",
      photo_url: p.photo_url || "",
      working_days: p.working_days || [],
      service_ids: p.service_ids || [],
      active: p.active !== false,
    });
  };
  const cancelEdit = () => { setEditing(null); setError(""); };

  const toggleDay = (d) => setDraft((cur) => ({
    ...cur,
    working_days: cur.working_days.includes(d)
      ? cur.working_days.filter((x) => x !== d)
      : [...cur.working_days, d],
  }));

  const save = async () => {
    setError("");
    if (!draft.display_name.trim()) { setError("Display name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...draft, display_name: draft.display_name.trim(), bio: draft.bio.trim(), photo_url: draft.photo_url.trim() };
      if (editing === "new") await api.post("/practitioners", payload);
      else await api.put(`/practitioners/${editing}`, payload);
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive ${p.display_name}? They will be hidden from booking and day rules.`)) return;
    await api.delete(`/practitioners/${p.id}`);
    load();
  };

  const restore = async (p) => {
    await api.put(`/practitioners/${p.id}`, {
      display_name: p.display_name,
      bio: p.bio || "",
      photo_url: p.photo_url || "",
      working_days: p.working_days || [],
      service_ids: p.service_ids || [],
      active: true,
    });
    load();
  };

  const visible = practitioners.filter((p) => showInactive || p.active !== false);
  const inactiveCount = practitioners.filter((p) => p.active === false).length;

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Practitioners</p>
          <h1 className="font-display text-4xl text-charcoal">Roster &amp; weekly schedule</h1>
          <p className="text-sm font-ui text-slate mt-1">One practitioner per weekday — clients booking that day will see them automatically.</p>
        </div>
        <button onClick={startNew} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md inline-flex items-center gap-1.5" data-testid="new-practitioner">
          <Plus className="w-4 h-4" /> New practitioner
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm p-6 mb-6" data-testid="practitioner-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-ui font-semibold text-charcoal">{editing === "new" ? "New practitioner" : "Edit practitioner"}</h2>
            <button onClick={cancelEdit} className="p-1 text-slate hover:text-charcoal"><X className="w-4 h-4" /></button>
          </div>
          {error && <div className="mb-3 px-3 py-2 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-xs font-ui text-status-danger" data-testid="practitioner-form-error">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <label className="block col-span-2 sm:col-span-1">
              <span className="block text-xs font-ui text-slate mb-1">Display name *</span>
              <input className={inputCls} value={draft.display_name} onChange={(e) => setDraft({ ...draft, display_name: e.target.value })} placeholder="Kendra W." data-testid="pract-display-name" />
            </label>
            <label className="block col-span-2 sm:col-span-1">
              <span className="block text-xs font-ui text-slate mb-1">Photo URL</span>
              <input className={inputCls} value={draft.photo_url} onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })} placeholder="https://…" data-testid="pract-photo-url" />
            </label>
            <label className="block col-span-2">
              <span className="block text-xs font-ui text-slate mb-1">Bio</span>
              <textarea rows={3} className={inputCls} value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} placeholder="Short bio shown on booking and practitioner pages." data-testid="pract-bio" />
            </label>
            <div className="col-span-2">
              <span className="block text-xs font-ui text-slate mb-1.5">Working days <span className="text-[10px] font-mono text-slate">(also synced when you change weekly schedule below)</span></span>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(d)}
                    className={`px-3 py-1.5 rounded-pill text-xs font-ui border transition ${draft.working_days.includes(d) ? "bg-forest text-white border-forest" : "border-hairline text-charcoal hover:bg-cream"}`}
                    data-testid={`pract-day-${d}`}
                  >
                    {WEEKDAY_SHORT[d]}
                  </button>
                ))}
              </div>
            </div>
            <label className="col-span-2 inline-flex items-center gap-2 text-sm font-ui text-charcoal">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="rounded border-hairline text-forest focus:ring-sage" data-testid="pract-active" />
              Active (visible in booking and day rules)
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={cancelEdit} className="px-4 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream">Cancel</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md disabled:opacity-40" data-testid="pract-save">
              {busy ? "Saving…" : editing === "new" ? "Create" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden">
          <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
            <h2 className="font-ui font-semibold text-charcoal">Roster <span className="text-xs font-mono text-slate ml-1">({visible.length})</span></h2>
            {inactiveCount > 0 && (
              <button
                onClick={() => setShowInactive(!showInactive)}
                className="text-xs font-ui text-slate hover:text-forest"
                data-testid="toggle-inactive"
              >
                {showInactive ? "Hide archived" : `Show archived (${inactiveCount})`}
              </button>
            )}
          </div>
          <ul className="divide-y divide-hairline" data-testid="practitioners-list">
            {visible.length === 0 && <li className="px-6 py-12 text-center text-sm font-ui text-slate">No practitioners yet. Add your first one.</li>}
            {visible.map((p) => {
              const inactive = p.active === false;
              return (
                <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-4" data-testid={`practitioner-row-${p.id}`}>
                  <div className="flex items-start gap-4 min-w-0">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt="" className={`w-12 h-12 rounded-pill object-cover shrink-0 ${inactive ? "opacity-50 grayscale" : ""}`} />
                    ) : (
                      <div className={`w-12 h-12 rounded-pill bg-cream flex items-center justify-center text-forest font-medium shrink-0 ${inactive ? "opacity-50" : ""}`}>{p.display_name?.[0]}</div>
                    )}
                    <div className="min-w-0">
                      <p className="font-ui text-sm font-medium text-charcoal flex items-center gap-2">
                        {p.display_name}
                        {inactive && <span className="text-[10px] font-mono uppercase tracking-wider text-slate bg-slate/15 px-1.5 py-0.5 rounded-pill">archived</span>}
                      </p>
                      {p.bio && <p className="text-xs font-ui text-slate mt-0.5 line-clamp-2">{p.bio}</p>}
                      <p className="text-xs font-mono text-slate mt-2 uppercase tracking-wider">Working: {p.working_days?.join(" · ") || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => startEdit(p)} className="p-1.5 text-slate hover:text-forest hover:bg-cream rounded-md" title="Edit" data-testid={`edit-pract-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    {inactive ? (
                      <button onClick={() => restore(p)} className="p-1.5 text-slate hover:text-forest hover:bg-cream rounded-md" title="Restore" data-testid={`restore-pract-${p.id}`}>
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => archive(p)} className="p-1.5 text-slate hover:text-status-danger hover:bg-status-danger-bg/40 rounded-md" title="Archive" data-testid={`archive-pract-${p.id}`}>
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden">
          <div className="px-6 py-4 border-b border-hairline">
            <h2 className="font-ui font-semibold text-charcoal">Weekly schedule</h2>
            <p className="text-xs font-ui text-slate mt-1">Pick which practitioner is on duty each weekday. Applies to every bookable service.</p>
          </div>
          <ul className="divide-y divide-hairline" data-testid="day-rules-editor">
            {WEEKDAYS.map((wd) => (
              <li key={wd} className="px-6 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-ui text-sm text-charcoal">{WEEKDAY_LABELS[wd]}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-slate mt-0.5">{wd}</p>
                </div>
                <select
                  value={findRule(wd)}
                  onChange={(e) => setRule(wd, e.target.value)}
                  className="px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30 min-w-[180px]"
                  data-testid={`rule-${wd}`}
                >
                  <option value="">— Closed —</option>
                  {practitioners.filter((p) => p.active !== false).map((p) => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
